import { gql } from '@apollo/client';

import { BagStatus } from '../../bags/graphql/types/BagTagType';
import { BatchBag, BatchLog, BatchLogType, CreationMethod } from '../types';

/** 批次详情的两个查询共用的入参（Long 以字符串传） */
export interface SortingBatchDetailVariables {
    warehouseId: string;
    batchId: string;
}

export interface SortingBatchBagType {
    bagId: string;
    bagNumber: string;
    sortingCode: string | null;
    /** 包牌原有状态值 */
    status: BagStatus;
    creationMethod: CreationMethod;
    /** 批次记录口径的包裹数 */
    parcelCount: number;
}

export interface SortingBatchBagsType {
    bags: SortingBatchBagType[];
    createdBagCount: number;
    usedBagCount: number;
    /** 含结束分拣时废弃的未使用包牌 */
    unusedBagCount: number;
    parcelCount: number;
}

export const GET_SORTING_BATCH_BAGS = gql`
    query GetSortingBatchBags($warehouseId: Long!, $batchId: Long!) {
        sortingBatchBags(warehouseId: $warehouseId, batchId: $batchId) {
            bags {
                bagId
                bagNumber
                sortingCode
                status
                creationMethod
                parcelCount
            }
            createdBagCount
            usedBagCount
            unusedBagCount
            parcelCount
        }
    }
`;

export interface GetSortingBatchBagsResponse {
    sortingBatchBags: SortingBatchBagsType;
}

/** 后端只填各类型用到的字段，其余为 null */
export interface SortingBatchLogType {
    type: BatchLogType;
    /** epoch millis（Long 以字符串形式返回） */
    time: string;
    operatorName: string | null;
    /** 账号角色（AppRole 名） */
    operatorRole: string | null;
    siteName: string | null;
    /** 操作来源：PDA_BAGGING / CROSS_BELT_DROP / BAG_MERGE / BAG_CHANGE / UNBAGGING / BATCH_MOVE_IN / BATCH_MOVE_OUT / BATCH_CANCEL / BATCH_SORTING_END / BATCH_COMPLETE */
    source: string | null;
    bagNumber: string | null;
    parcelNumber: string | null;
    parcelCount: number | null;
    targetBagNumber: string | null;
    sourceBatchName: string | null;
    targetBatchName: string | null;
    syncByBatchName: string | null;
    /** 分拣机自动创建批次时的分拣机编号 */
    sorterCode: string | null;
}

export const GET_SORTING_BATCH_OPERATION_LOGS = gql`
    query GetSortingBatchOperationLogs($warehouseId: Long!, $batchId: Long!) {
        sortingBatchOperationLogs(warehouseId: $warehouseId, batchId: $batchId) {
            type
            time
            operatorName
            operatorRole
            siteName
            source
            bagNumber
            parcelNumber
            parcelCount
            targetBagNumber
            sourceBatchName
            targetBatchName
            syncByBatchName
            sorterCode
        }
    }
`;

export interface GetSortingBatchOperationLogsResponse {
    sortingBatchOperationLogs: SortingBatchLogType[];
}

/** 后端包牌 → 包牌表行 */
export const toBatchBag = (bag: SortingBatchBagType, batchNumber: string): BatchBag => ({
    bagId: bag.bagId,
    bagNumber: bag.bagNumber,
    sortingCode: bag.sortingCode || '',
    status: bag.status,
    creationMethod: bag.creationMethod,
    parcels: bag.parcelCount,
    batchNumber,
});

/** 后端操作历史 → 时间线条目；空值转 undefined，时间线对缺省字段不展示 */
export const toBatchLog = (log: SortingBatchLogType, batchNumber: string): BatchLog => ({
    batchNumber,
    type: log.type,
    time: Number(log.time),
    operator: log.operatorName ?? undefined,
    operatorRole: log.operatorRole ?? undefined,
    site: log.siteName ?? undefined,
    source: log.source ?? undefined,
    bagNumber: log.bagNumber ?? undefined,
    parcelNumber: log.parcelNumber ?? undefined,
    parcelCount: log.parcelCount ?? undefined,
    targetBagNumber: log.targetBagNumber ?? undefined,
    sourceBatch: log.sourceBatchName ?? undefined,
    targetBatch: log.targetBatchName ?? undefined,
    syncByBatch: log.syncByBatchName ?? undefined,
    sorterNo: log.sorterCode ?? undefined,
});
